import { toast } from "react-toastify";
import { uploadMedia } from "../../services/mediaLibraryService";
import { extractApiMessage } from "./externalImportCropUtils";

export const MEDIA_DROP_ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp"];

export const MEDIA_DROP_MAX_BYTES = 2 * 1024 * 1024;

const MIME_BY_EXT = {
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".webp": "image/webp",
};

const nullSafeText = (value) => (value == null ? "" : String(value));

const extractUploaded = (res) => {
  const data = res?.data;
  return data?.result ?? data?.Result ?? data?.data ?? data ?? null;
};

export const normalizeUploadedMedia = (raw) => {
  if (!raw) return null;
  return {
    mediaAssetId:
      raw.mediaAssetId ?? raw.id ?? raw._id ?? raw.mediaId ?? raw.assetId,
    mediaUrl: raw.mediaUrl ?? raw.url ?? raw.fileUrl ?? raw.path ?? "",
    thumbnailUrl:
      raw.thumbnailUrl ??
      raw.thumbUrl ??
      raw.thumbnail ??
      raw.mediaUrl ??
      raw.url ??
      "",
    mediaName:
      nullSafeText(raw.mediaName ?? raw.name ?? raw.fileName ?? raw.title) ||
      "Untitled",
    providerType: raw.providerType ?? raw.provider ?? raw.source ?? "Upload",
    width: raw.width ?? raw.imageWidth ?? null,
    height: raw.height ?? raw.imageHeight ?? null,
  };
};

export const validateDroppedMediaFile = (file) => {
  if (!file) {
    return { valid: false, message: "No file selected." };
  }

  const ext = `.${String(file.name || "")
    .split(".")
    .pop()
    ?.toLowerCase()}`;

  if (!MEDIA_DROP_ALLOWED_EXTENSIONS.includes(ext)) {
    return {
      valid: false,
      message: "Only JPG, JPEG, PNG and WEBP files are allowed.",
    };
  }

  const expectedMime = MIME_BY_EXT[ext];
  if (file.type && expectedMime && file.type !== expectedMime) {
    return {
      valid: false,
      message: "Only JPG, JPEG, PNG and WEBP files are allowed.",
    };
  }

  if (file.size > MEDIA_DROP_MAX_BYTES) {
    return {
      valid: false,
      message: "Image size must be less than 2 MB.",
    };
  }

  return { valid: true, message: "" };
};

/** Upload a raw file to Media Library (multipart field: MediaFile). */
export const uploadRawFileToMediaLibrary = async (file) => {
  const formData = new FormData();
  formData.append("MediaFile", file, file.name);
  const res = await uploadMedia(formData);
  const uploaded = normalizeUploadedMedia(extractUploaded(res));
  if (!uploaded?.mediaUrl && !uploaded?.thumbnailUrl) {
    throw new Error("Upload succeeded but no image URL was returned.");
  }
  return uploaded;
};

export const uploadDroppedMediaFile = async (file) => {
  const validation = validateDroppedMediaFile(file);
  if (!validation.valid) {
    toast.error(validation.message);
    return null;
  }

  try {
    const uploaded = await uploadRawFileToMediaLibrary(file);
    return uploaded;
  } catch (err) {
    toast.error(extractApiMessage(err) || "Upload failed. Please try again.");
    return null;
  }
};
